// ========================================
// Ant Design Theme Configuration - CU launcher palette (WIRED)
// ========================================
// Interface colors come verbatim from the CU launcher palette
// (src/theme/cuPalette.js - host provenance and scope decisions are documented
// there). Geometry (typography, radii, control heights, spacing, shadows,
// z-index) is preserved from the legacy builder theme.
//
// Deliberate token decisions:
// - Status colors: ONLY the AntD seeds are declared (same seeds antd 6.6.2
//   defaults to); hover/active states are derived at runtime by the SAME
//   installed antd the CU host runs, which declares no status tokens either.
// - Global colorBorderSecondary is NOT declared (the CU host declares none):
//   antd derives the light default #F0F0F0. Card and Tabs keep the CU host's
//   component-scoped values instead.
// - Components: geometry-only legacy config merged per component with the CU
//   host component color overrides (never a shallow spread that drops keys).
// - Legacy overrides that defeated the global tokens were removed (forced
//   Input text, forced teal borders, transparent Menu selection, Slider/Pagination
//   colors) - those states now derive from the CU tokens.
// ========================================

import { CU_ANTD_SEED_STATUS, CU_LAUNCHER_COMPONENT_OVERRIDES, CU_LAUNCHER_TOKENS } from "./cuPalette";

// Legacy Input focus-ring geometry, recolored with the canonical CU outline token.
const inputActiveShadow = `0 0 0 2px ${CU_LAUNCHER_TOKENS.controlOutline}`;

// Component geometry preserved from the legacy theme. Colors derive from the
// global CU tokens unless the CU host declares a component-scoped override
// (merged below).
const componentGeometry = {
	Button: {
		borderRadius: 4,
		controlHeight: 32,
		controlHeightLG: 40,
		controlHeightSM: 24,
	},
	Input: {
		borderRadius: 4,
		activeShadow: inputActiveShadow,
	},
	Select: {
		borderRadius: 4,
	},
	Form: {
		itemMarginBottom: 24,
		verticalLabelPadding: "0 0 8px",
	},
	Modal: {
		borderRadius: 4,
	},
	Notification: {
		zIndexPopup: 9999999, // From _shame.scss
	},
	Table: {
		fontFamily: "'Rubik', 'Helvetica Neue Light', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
		borderRadius: 4,
	},
	Dropdown: {
		borderRadius: 4,
	},
};

// Merge the CU host component color overrides into the geometry config, one
// component at a time so no geometry key is ever dropped.
const components = { ...componentGeometry };
Object.entries(CU_LAUNCHER_COMPONENT_OVERRIDES).forEach(([component, colorTokens]) => {
	components[component] = { ...components[component], ...colorTokens };
});

const antdTheme = {
	token: {
		// CU launcher interface colors (host-exact, 62 tokens).
		...CU_LAUNCHER_TOKENS,

		// AntD seed status colors only - derived hover/active states come from
		// the same installed antd 6.6.2 the CU host runs.
		...CU_ANTD_SEED_STATUS,

		// ========================================
		// Typography (geometry - preserved from the legacy theme)
		// ========================================
		fontFamily: "'Rubik', 'Helvetica Neue Light', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
		fontSize: 14,
		fontSizeHeading1: 38,
		fontSizeHeading2: 30,
		fontSizeHeading3: 24,
		fontSizeHeading4: 20,
		fontSizeHeading5: 16,

		// ========================================
		// Layout & Spacing (geometry - preserved)
		// ========================================
		borderRadius: 4,
		borderRadiusLG: 4,
		borderRadiusSM: 4,

		// Shadows (from $box-shadow - preserved)
		boxShadow: "0 2px 18px rgba(0, 0, 0, 0.06)",
		boxShadowSecondary: "0 2px 8px rgba(0, 0, 0, 0.15)",

		// Control heights (preserved)
		controlHeight: 32,
		controlHeightLG: 40,
		controlHeightSM: 24,
	},
	components,
};

export default antdTheme;
